/** Browser clipboard with graceful fallbacks (embed/webview friendly). */

export type CopyResult =
  | { ok: true; method: 'async' | 'exec' }
  | { ok: false; error: unknown }

/**
 * Copy text to clipboard.
 * 1. navigator.clipboard.writeText (secure context + permission)
 * 2. legacy document.execCommand('copy') via temp textarea
 * (execCommand works in many embedded webviews where async API rejects.)
 */
export async function copyText(text: string): Promise<CopyResult> {
  const t = String(text ?? '')

  // 1) Modern async API
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(t)
      return { ok: true, method: 'async' }
    } catch {
      /* fall through to legacy */
    }
  }

  // 2) Legacy execCommand fallback
  if (typeof document !== 'undefined' && typeof document.execCommand === 'function') {
    try {
      const ta = document.createElement('textarea')
      ta.value = t
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.top = '-1000px'
      ta.style.opacity = '0'
      ta.style.pointerEvents = 'none'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      ta.setSelectionRange(0, t.length)
      const ok = document.execCommand('copy')
      ta.remove()
      if (ok) return { ok: true, method: 'exec' }
      return { ok: false, error: new Error('execCommand(copy) returned false') }
    } catch (e) {
      return { ok: false, error: e }
    }
  }

  return { ok: false, error: new Error('no clipboard API available') }
}